<template>
  <div class="page-get-started">
    <Banner type="secondary">
      <h1>You've got the will</h1>
      <h2>We've got the way</h2>
    </Banner>

    <CreateWebsite
      ref="siteCreator"
      :template="selectedCategory"
      :goToSite="false"
      :job="formPayload"
      :companyId="companyId"
      :siteName="siteName"
      :companyCity="companyCity"
      @jobStarted="jobStarted"
    />

    <div class="container chat-wrapper">
      <div class="chatbox">
        <div class="chat-header">
          <div class="bot-details">
            <div class="avatar-wrapper">
              <LazyImage
                :url="require('../../assets/probot-avatar.svg')"
                class="avatar"
              />
            </div>
            <span class="name">ProBot</span>
          </div>

          <ChatProgress
            v-if="!simpleMode"
            :total="totalQuestions"
            :step="currentQuestion"
          />
        </div>

        <transition name="fade" mode="out-in">
          <div
            key="simple-form"
            class="chat-inner simple-mode"
            v-if="simpleMode">
            <TextInput
              label="Website Name"
              v-model="siteName"
              @input="$v.siteName.$touch()"
            />

            <TextInput
              label="Company Phone"
              v-model="companyPhone"
              @input="$v.companyPhone.$touch()"
            />

            <TextInput
              label="Company City"
              v-model="companyCity"
              @input="$v.companyCity.$touch()"
            />

            <div class="text-input">
              <select v-model="selectedCategorySlug">
                <option selected value="" disabled>Select a Category</option>
                <option
                  v-for="(category, index) in categories"
                  :key="`${category.slug}-${index}`"
                  :value="category.slug"
                  >{{ category.name }}</option
                >
              </select>
            </div>

            <div class="create-site-btn">
              <Btn
                isBlock
                primary
                :isLoading="loading"
                @click="createSite">
                Create My Site
              </Btn>
            </div>
          </div>

          <div
            v-else
            key="chat-form"
            class="chat-inner"
            :class="{'categories-active': categoriesActive}"
          >
            <div class="chat-scroll">
              <transition-group name="scale">
                <ChatMessage
                  v-for="message in script"
                  :key="message"
                  :message="message"
                />
              </transition-group>
              <span id="chat-anchor" ref="chatAnchor"></span>
            </div>

            <form
              @submit.prevent="answerQuestion"
              :class="{'is-disabled': disableInput}"
            >
              <div class="chat-input" :class="{'is-visible': chatInputVisible}">
                <ChatLoader v-if="!chatInputVisible" />

                <label v-if="helperMessage.message" class="helper-message">{{
                  helperMessage.message
                }}</label>

                <div class="chat-input-wrapper">
                  <component
                    @input="updateAnswer"
                    @categoriesActive="setCategoriesActive"
                    v-if="!lastQuestion"
                    ref="chatInput"
                    :is="questionType"
                    :suggestion="suggestion"
                    :options="questionOptions"
                    :placeholder="helperMessage.placeholder"
                  />
                </div>

                <Btn
                  isBlock
                  primary
                  :isLoading="loading" >
                  {{ chatText }}
                </Btn>
              </div>
            </form>
          </div>
        </transition>

        <div class="restart-btn">
          <p v-if="isCreating">Creating your website... About {{secondsRemaining}} seconds remaining!</p>
          <div v-else>
            <Btn subtle v-if="!simpleMode" @click="restart">Start over</Btn>
            <Btn subtle v-if="!simpleMode" @click="setSimpleMode(true)">Simple Mode</Btn>
            <Btn subtle v-else @click="setSimpleMode(false)">Chat Mode</Btn>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {readCompanyData} from '@/lib/companies'
import {readTemplatesData, readTemplateDataBySlug} from '@/lib/templates'

import {validationMixin} from 'vuelidate'
import {required} from 'vuelidate/lib/validators'
import {errorMessages} from '@/lib/error_messages'
import {VueTyper} from 'vue-typer'

import {mapGetters, mapState} from 'vuex'

import Page from '@/mixins/page'

import Banner from '@/components/Banner'
import LazyImage from '@/components/LazyImage'
import TextInput from '@/components/forms/inputs/TextInput'
import ChatProgress from '@/components/chat/ChatProgress'
import ChatMessage from '@/components/chat/ChatMessage'
import ChatLoader from '@/components/chat/ChatLoader'
import Text from '@/components/chat/Text'
import Category from '@/components/chat/Category'
import CreateWebsite from '@/components/SiteSetup/CreateWebsite'
import Btn from '@/components/Btn'

const TIMEOUT = 2000

export default {
  name: 'GetStarted',

  mixins: [validationMixin, Page],

  data() {
    return {
      secondsRemaining: 30,
      currentQuestion: 1,
      currentAnswer: '',
      siteName: '',
      companyCity: '',
      companyPhone: '',
      selectedCategorySlug: '',
      selectedCategory: {},
      script: [],
      errors: {},
      company: {},
      categories: [],
      disableInput: false,
      chatInputVisible: false,
      categoriesActive: false,
      simpleMode: false,
      isCreating: false,
      title: 'Create your website'
    }
  },

  props: {
    companyId: String
  },

  components: {
    Banner,
    TextInput,
    ChatProgress,
    ChatMessage,
    ChatLoader,
    'chat-text': Text,
    VueTyper,
    Category,
    LazyImage,
    CreateWebsite,
    Btn
  },

  computed: {
    ...mapGetters(['loading']),
    ...mapState(['currentUser']),
    ...errorMessages([
      'siteName',
      'companyPhone',
      'companyCity',
      'selectedCategorySlug'
    ]),

    firstName() {
      let name = this.currentUser.name || ''
      return name.split(' ')[0]
    },

    selectedCategoryHuman() {
      return this.selectedCategorySlug.replace(/-/g, ' ')
    },

    companyEmail() {
      return this.company.email
    },

    companyOwnerId() {
      return this.company.owner_id
    },

    totalQuestions() {
      return this.questions.length
    },

    timeOfDay() {
      let today = new Date()
      let hr = today.getHours()

      if (hr < 12) {
        return 'morning'
      } else if (hr < 18) {
        return 'afternoon'
      } else {
        return 'evening'
      }
    },

    lastQuestion() {
      return this.currentQuestion === this.totalQuestions
    },

    questionType() {
      return this.questions[this.currentQuestion - 1].type
    },

    questionModel() {
      return this.questions[this.currentQuestion - 1].model
    },

    questionOptions() {
      return this.questions[this.currentQuestion - 1].options
    },

    chatText() {
      if (this.loading) {
        return 'Please wait...'
      } else {
        return this.lastQuestion ? 'Create My Website' : 'Send'
      }
    },

    preventSubmit() {
      return this.currentAnswer === ''
    },

    questions() {
      return [
        {
          content: `Hello ${this.firstName}, I’m ProBot. I’m going to be helping you create your new website this ${this.timeOfDay}.
            <br><br>
            <strong>Could you please start by sending me the name for your new website?</strong>`,
          type: 'chat-text',
          model: 'siteName',
          options: null
        },
        {
          content: `Okay, Could you please send me the phone number for ${this.siteName}?</strong>`,
          type: 'chat-text',
          model: 'companyPhone',
          options: null
        },
        {
          content: `Perfect, thanks! I’m going to add this to your website so your customers can contact you easily. This can be changed later. Just a couple more questions before I create the site.
            <br><br>
            <strong>What town/city is ${this.siteName} based in?</strong>`,
          type: 'chat-text',
          model: 'companyCity',
          options: null
        },
        {
          content: `Great. Please now <strong>select which category ${this.siteName} falls under</strong>. If you can't find your category, select <strong>not listed</strong>.`,
          type: 'category',
          model: 'selectedCategorySlug',
          options: this.categories
        },
        {
          content: `Okay! ${this.siteName}... ${this.selectedCategoryHuman}... ${this.companyCity} <span class="emoji">🚀</span> Ready when you are!`
        }
      ]
    },

    helperMessages() {
      return [
        {
          message: 'Your Website Name:',
          placeholder: `e.g. ${this.firstName}'s Great Website`
        },
        {
          message: 'Your Phone Number:',
          placeholder: 'e.g. 07123 456789'
        },
        {
          message: 'Town/City:',
          placeholder: 'e.g. London'
        },
        {
          message: 'Please choose a category',
          placeholder: null,
          suggestion: 'category'
        },
        {
          message: `I'm ready to create your website!`,
          placeholder: null
        }
      ]
    },

    helperMessage() {
      return this.helperMessages[this.currentQuestion - 1]
    },

    suggestion() {
      let type = this.helperMessage.suggestion
      let param = this.$route.query[type]
      return param
    },

    formPayload() {
      return {
        userId: this.companyOwnerId,
        siteName: this.siteName,
        companyPhone: this.companyPhone,
        companyOwnerId: this.companyOwnerId,
        createSubscription: true,
        seoDescription:
          'First class business in the local area. Professional and friendly service. Hundreds of satisfied customers.',
        sitePageNames: ['Home', 'Services', 'Gallery', 'Reviews', 'Contact'],
        sitePageSlideHeadings: [
          'Welcome',
          'Our Services',
          'Gallery',
          'Our Reviews',
          'Contact Us'
        ],
        sitePagePaths: ['/', '/services', '/gallery', '/reviews', '/contact']
      }
    }
  },

  methods: {
    getData() {
      readTemplatesData().then(({data}) => {
        this.categories = data
      })

      readCompanyData(this.companyId).then(({data}) => {
        this.company = data
      })
    },

    jobStarted() {
      this.isCreating = true
      this.handleCountdown()
    },

    handleCountdown() {
      let seconds = 30

      let timer = setInterval(() => {
        seconds = --seconds
        this.secondsRemaining = seconds

        if (seconds === 0) {
          clearInterval(timer)
        }
      }, 1000)

      return timer
    },

    setSimpleMode(value) {
      this.simpleMode = value
    },

    setCategoriesActive(value) {
      this.categoriesActive = value
    },

    answerQuestion() {
      if (this.lastQuestion) {
        this.createSite()
      } else {
        if (!this.disableInput && !this.preventSubmit) {
          this.categoriesActive = false
          this.storeAnswer(this.currentAnswer)

          const readableAnswer = this.currentAnswer.replace(/-/g, ' ')

          this.script.push(readableAnswer)
          this.chatInputVisible = false

          window.setTimeout(() => {
            this.currentQuestion++
          }, TIMEOUT)

          this.botReply()
        }
      }

      window.setTimeout(() => {
        this.scrollDown()
      }, 250)
    },

    scrollDown() {
      this.$refs.chatAnchor.scrollIntoView()
    },

    restart() {
      this.currentQuestion = 1
      this.script = this.script.splice(0, 1)
    },

    updateAnswer(answer) {
      this.currentAnswer = answer
    },

    storeAnswer(answer) {
      this[this.questionModel] = answer
    },

    startChat() {
      const first = 0
      this.script.push(this.questions[first].content)
      this.chatInputVisible = true
    },

    botReply() {
      window.setTimeout(() => {
        this.disableInput = false
        this.script.push(this.questions[this.currentQuestion - 1].content)
        this.chatInputVisible = true
        this.currentAnswer = ''

        if (!this.lastQuestion) {
          this.$refs.chatInput.clearValue()
        }
      }, TIMEOUT)

      window.setTimeout(() => {
        this.scrollDown()
      }, TIMEOUT + 250)

      this.disableInput = true
    },

    getCategoryData() {
      readTemplateDataBySlug(this.selectedCategorySlug)
        .then(({data}) => {
          this.selectedCategory = data
        })
    },

    createSite() {
      this.$refs.siteCreator.startJob()
    }
  },

  validations: {
    siteName: {required},
    companyPhone: {required},
    companyCity: {required},
    selectedCategorySlug: {required},
    firstName: {required}
  },

  mounted() {
    this.getData()

    if (!this.simpleMode) {
      window.setTimeout(() => {
        this.startChat()
      }, TIMEOUT / 2)
    } else {
      let param = this.$route.query.category

      if (param) {
        this.selectedCategory = param
      }
    }
  },

  watch: {
    selectedCategorySlug() {
      this.getCategoryData()
    }
  }
}
</script>

<style lang="sass">
@import '../../stylesheets/base/variables'
@import '../../stylesheets/helpers/mixins'
@import '../../stylesheets/helpers/extends'

.page-get-started
  .banner-content
    margin-bottom: $gap-large

  .chat-wrapper
    position: relative
    margin: -50px auto $gap-large

.emoji
  text-shadow: 0 0 5px rgb(35, 35, 35)

.bot-details
  display: flex
  align-items: center

.restart-btn
  margin: $gap-medium auto 0
  text-align: center

  p
    font-size: 14px
    background: linear-gradient(to right, #898989, #e08236)
    background-size: 200% auto
    color: #898989
    background-clip: text
    text-fill-color: transparent
    -webkit-background-clip: text
    -webkit-text-fill-color: transparent
    animation: textLoader 30s 5s linear
    font-weight: bold
    text-transform: uppercase

  .btn
    background: rgba(0, 0, 0, 0.2)
    font-size: 10px
    margin: 0 $gap-small

.robot-image
  display: none
  @extend %theme-filter

  @include media($media-gt-large)
    display: block
    margin: $gap-medium
    position: relative

.scale-enter-active, .scale-leave-active
  transition: all .5s ease
  transform: scale(1)
  transform-origin: bottom

.scale-enter, .scale-leave-to
  opacity: 0
  transform: scale(0)

.chat-anchor
  width: 1px
  height: 1px
  position: absolute
  bottom: 0px
  left: 0

.chatbox
  @extend %card
  min-height: 675px
  max-width: 600px
  border-radius: 0
  width: 100%
  margin: 0 auto
  padding: $gap-small
  position: relative
  display: flex
  flex-direction: column
  justify-content: space-between

  @include media($media-gt-medium)
    padding: $gap-medium
    border-radius: 5px

  .chat-header
    background: #efefef
    text-align: center
    padding: $gap-small
    display: flex
    align-items: center
    justify-content: space-between

    .name
      margin-left: 15px
      font-family: $font-bold
      text-transform: uppercase
      letter-spacing: 1px
      animation: scaleIn 1s .5s cubic-bezier(0.65, 0.05, 0.14, 1.3)
      animation-fill-mode: forwards
      opacity: 0

    .avatar-wrapper
      animation: scaleIn 1s .25s cubic-bezier(0.65, 0.05, 0.14, 1.3)
      animation-fill-mode: forwards
      opacity: 0

    .avatar
      background: #6887a0
      padding: 8px
      border-radius: 50%
      width: 50px
      height: 50px

  .chat-scroll
    height: 379px
    overflow: auto

    .message
      &:first-child
        margin-top: $gap-small

  .chat-inner
    background: #f7f7f7
    padding: 0 $gap-small $gap-small
    flex-grow: 1
    display: flex
    flex-direction: column
    justify-content: space-between
    overflow: hidden
    position: relative

    &:before
      content: ''
      pointer-events: none
      opacity: 0
      transition: opacity .3s .5s ease-in-out
      background: rgba(204, 204, 204, 0.9)
      position: absolute
      border-bottom-right-radius: 5px
      border-bottom-left-radius: 5px
      top: 0
      left: 0
      width: 100%
      height: 100%

    &.categories-active
      &:before
        opacity: 1
        transition: opacity .3s ease-in-out

    &.simple-mode
      display: block
      position: relative

      .btn
        margin: $gap-large auto
        display: block
        font-size: 14px

  .create-site-btn
    text-align: center

  .chat-input
    background: #fff
    padding: $gap-small
    box-shadow: 0 0 15px rgba(0, 0, 0, 0.05)
    border-radius: 5px
    transition: opacity .5s .2s ease-in-out
    animation: slideUp 1s 1.5s cubic-bezier(0.65, 0.05, 0.14, 1.3)
    animation-fill-mode: forwards
    opacity: 0
    z-index: 1

    label, input, .btn
      opacity: 0
      pointer-events: none
      transition: opacity .2s ease-in-out

    &.is-visible
      label, input, .btn
        opacity: 1
        pointer-events: all

    &.is-disabled
      opacity: 0.25
      pointer-events: none

    @include media($media-gt-x-large)
      display: flex
      align-items: center
      justify-content: space-between

    .chat-input-wrapper
      flex-grow: 1

    .btn
      width: 100%

      @include media($media-gt-x-large)
        width: auto

    .helper-message
      min-width: 140px
      text-align: center
      margin: 0
      display: block
      padding: $gap-small 0

      @include media($media-gt-x-large)
        padding: 0
</style>
